import { styled } from "@mui/material";
import Loader from "_shared/_views/loading/loader";
import Guid from "_shared/guids/guid";
import { CanceledError } from "axios";
import MessagingAPIService from "messaging/_api/messaging-api-service";
import ForumQuery, { ForumFilterParameters, ForumOrderParameters } from "messaging/forums/_api/queries/forum-query";
import ForumChatWindow from "messaging/forums/_views/components/forum-chat-window";
import Forum from "messaging/forums/forum";
import { enqueueSnackbar } from "notistack";
import { useEffect, useState } from "react";
import { useSession } from "users/__session/session-context";
import ForumSelection from "./forum-selector";

const ForumContainer = styled("div")(({ theme }) => ({
  display: "grid",
  gridTemplateRows: "auto 1fr",
  paddingTop: theme.spacing(1),
  width: "100%",
}));
const SelectionContainer = styled("div")(() => ({
  display: "flex",
  gridRowStart: 1,
}));
const ChatContainer = styled("div")(() => ({
  gridRowStart: 2,
  gridRowEnd: "none",
}));
const LoaderContainer = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  height: "100%",
  padding: theme.spacing(4),
  paddingTop: 0,
  width: "100%",
}));

type ChatDialogProps = {
  forumId?: Guid;
  forums?: Forum[];
  getAllForums?: boolean;
  loadedForum?: Forum;
  onLeaveConversation?: () => void;
};

export default function ChatDialog(props: Readonly<ChatDialogProps>) {
  const { forumId, forums, getAllForums, loadedForum, onLeaveConversation } = props;

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [allForums, setAllForums] = useState<Forum[]>([]);
  const [individualForums, setIndividualForums] = useState<Forum[]>([]);
  const [teamForums, setTeamForums] = useState<Forum[]>([]);
  const [proposalForums, setProposalForums] = useState<Forum[]>([]);
  const [workForums, setWorkForums] = useState<Forum[]>([]);
  const [selectedForum, setSelectedForum] = useState<Forum | undefined>();
  const session = useSession();

  const loadChatDialog = async (abortController: AbortController) => {
    try {
      setIsLoading(true);

      if (loadedForum) {
        setSelectedForum(loadedForum);
        return;
      }

      const filterParameters = new ForumFilterParameters({ forumId });
      const orderParameters = new ForumOrderParameters();
      const request = new ForumQuery({
        filterParameters,
        orderParameters,
      });
      const messagingService = new MessagingAPIService(session); // TODO: use store
      const forumsResponse = await messagingService.getForums(request, abortController);
      const forums = Forum.fromJSONArray(forumsResponse);
      if (forumId) {
        if (!forums?.[0].id?.isEqualTo(forumId)) throw new Error("Forum not found");
        setSelectedForum(forums[0]);
        return;
      }
      if (!forums) {
        return;
      }
      setAllForums(forums);
      setIndividualForums(
        forums.filter(
          (forum) =>
            forum.topic?.entityClass &&
            ["UserNetworkConnection", "Marketplace.Individual"].includes(forum.topic.entityClass.value)
        )
      );
      setTeamForums(
        forums.filter(
          (forum) => forum.topic?.entityClass && ["Marketplace.Team"].includes(forum.topic.entityClass.value)
        )
      );
      setProposalForums(
        forums.filter((forum) => forum.topic?.entityClass && ["Work.Proposal"].includes(forum.topic.entityClass.value))
      );
      setWorkForums(
        forums.filter((forum) => forum.topic?.entityClass && ["Work"].includes(forum.topic.entityClass.value))
      );
    } catch (err) {
      if (err instanceof CanceledError) return;
      console.error(err);

      enqueueSnackbar("Failed to get contacts", { variant: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    let abortController = new AbortController();
    void loadChatDialog(abortController);

    return () => {
      abortController.abort();
      abortController = new AbortController();
    };
  }, []);

  return (
    <>
      {isLoading && (
        <LoaderContainer>
          <Loader />
        </LoaderContainer>
      )}
      {!isLoading && (
        <ForumContainer>
          {!forums && !getAllForums && !selectedForum && (
            <SelectionContainer>
              <ForumSelection
                individualForums={individualForums}
                teamForums={teamForums}
                proposalForums={proposalForums}
                workForums={workForums}
                onForumSelected={setSelectedForum}
              />
            </SelectionContainer>
          )}
          {!forums && getAllForums && (
            <ChatContainer>
              <ForumChatWindow forums={allForums} onLeaveConversation={onLeaveConversation} />
            </ChatContainer>
          )}
          {selectedForum && (
            <ChatContainer>
              <ForumChatWindow forums={[selectedForum]} onLeaveConversation={onLeaveConversation} />
            </ChatContainer>
          )}
          {forums && (
            <ChatContainer>
              <ForumChatWindow forums={forums} onLeaveConversation={onLeaveConversation} />
            </ChatContainer>
          )}
        </ForumContainer>
      )}
    </>
  );
}
